import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  BadgeCheck,
  CheckCircle2,
  LoaderCircle,
  Save,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import studentPortalService from '../../services/studentPortal.service';
import type { StudentPortalProfile, StudentPortalProfileUpdate } from '../../types/studentPortal';
import { portalApiError } from '../../utils/portalApiError';

const emptyProfile: StudentPortalProfile = {
  user_id: 0,
  first_name: '',
  last_name: '',
  full_name: '',
  email: '',
  phone: '',
  nationality: '',
  national_id: '',
  passport_number: '',
  address: '',
  is_complete: false,
  missing_fields: [],
};

export default function PortalProfile() {
  const { language } = useI18n();
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState<StudentPortalProfile>(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    studentPortalService.getProfile()
      .then((data) => { if (active) setProfile(data); })
      .catch((requestError: unknown) => { if (active) setError(portalApiError(requestError, language, { ar: 'تعذر تحميل الملف الشخصي.', en: 'Could not load your profile.' })); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [language]);

  const completion = useMemo(() => {
    const required = [profile.first_name, profile.last_name, profile.phone, profile.nationality];
    return Math.round((required.filter(Boolean).length / required.length) * 100);
  }, [profile]);

  const updateField = (field: keyof StudentPortalProfileUpdate, value: string) => {
    setSaved(false);
    setError(null);
    setProfile((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    const payload: StudentPortalProfileUpdate = {
      first_name: profile.first_name,
      last_name: profile.last_name,
      phone: profile.phone,
      nationality: profile.nationality,
      national_id: profile.national_id,
      passport_number: profile.passport_number,
      address: profile.address,
    };
    try {
      const updated = await studentPortalService.updateProfile(payload);
      setProfile(updated);
      await refreshUser();
      setSaved(true);
    } catch (requestError: unknown) {
      setError(portalApiError(requestError, language, { ar: 'تعذر حفظ بيانات الملف الشخصي.', en: 'Could not save your profile details.' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="portal-card portal-empty"><LoaderCircle className="portal-spin" /></div>;
  }

  return (
    <div className="portal-page portal-profile-page">
      <div className="portal-page-heading">
        <div>
          <span className="portal-eyebrow"><UserRound size={15} />{language === 'ar' ? 'بيانات حسابك' : 'Your account'}</span>
          <h1>{language === 'ar' ? 'الملف الشخصي' : 'Personal profile'}</h1>
          <p>{language === 'ar' ? 'بيانات بسيطة تساعد فريقنا على التواصل معك وتجهيز طلبك بدقة.' : 'Only the essential details our team needs to identify you and support your application.'}</p>
        </div>
        <span className={`portal-badge ${profile.is_complete ? 'success' : 'warning'}`}>
          {profile.is_complete ? <BadgeCheck size={15} /> : <AlertCircle size={15} />}
          {language === 'ar' ? `${completion}٪ مكتمل` : `${completion}% complete`}
        </span>
      </div>

      {error && <div className="portal-alert portal-alert-error"><AlertCircle size={19} /><div><strong>{language === 'ar' ? 'لم يتم الحفظ' : 'Changes were not saved'}</strong><p>{error}</p></div></div>}
      {saved && <div className="portal-alert portal-alert-success"><CheckCircle2 size={19} /><div><strong>{language === 'ar' ? 'تم حفظ بياناتك' : 'Profile updated'}</strong><p>{language === 'ar' ? 'ظهرت التغييرات في حسابك فورًا.' : 'Your changes are now reflected across your account.'}</p></div></div>}

      <div className="portal-grid portal-grid-main portal-profile-grid">
        <form className="portal-card portal-card-body portal-profile-form" onSubmit={submit}>
          <div className="portal-profile-section-heading">
            <span><UserRound size={20} /></span>
            <div><h2>{language === 'ar' ? 'المعلومات الشخصية' : 'Personal information'}</h2><p>{language === 'ar' ? 'يمكنك تعديل هذه البيانات في أي وقت.' : 'You can update these details at any time.'}</p></div>
          </div>

          <div className="portal-form-grid">
            <ProfileField label={language === 'ar' ? 'الاسم الأول' : 'First name'} value={profile.first_name || ''} onChange={(value) => updateField('first_name', value)} required />
            <ProfileField label={language === 'ar' ? 'اسم العائلة' : 'Last name'} value={profile.last_name || ''} onChange={(value) => updateField('last_name', value)} required />
            <ProfileField label={language === 'ar' ? 'البريد الإلكتروني' : 'Email address'} value={profile.email} type="email" readOnly hint={language === 'ar' ? 'البريد مرتبط بتسجيل الدخول ولا يتغير من هنا.' : 'Your login email is protected and cannot be changed here.'} />
            <ProfileField label={language === 'ar' ? 'رقم الهاتف' : 'Phone number'} value={profile.phone || ''} type="tel" onChange={(value) => updateField('phone', value)} required />
            <ProfileField label={language === 'ar' ? 'الجنسية' : 'Nationality'} value={profile.nationality || ''} onChange={(value) => updateField('nationality', value)} required />
            <ProfileField label={language === 'ar' ? 'الرقم القومي أو رقم الهوية' : 'National ID'} value={profile.national_id || ''} onChange={(value) => updateField('national_id', value)} />
            <ProfileField label={language === 'ar' ? 'رقم جواز السفر' : 'Passport number'} value={profile.passport_number || ''} onChange={(value) => updateField('passport_number', value)} />
            <label className="portal-field portal-field-wide">
              <span>{language === 'ar' ? 'العنوان' : 'Address'}</span>
              <textarea className="portal-textarea" value={profile.address || ''} onChange={(event) => updateField('address', event.target.value)} rows={4} />
            </label>
          </div>

          <div className="portal-profile-actions">
            <span><ShieldCheck size={16} />{language === 'ar' ? 'نحفظ بياناتك بشكل آمن ولا نعرضها للعامة.' : 'Your details are stored securely and are never public.'}</span>
            <button className="portal-button portal-button-primary" disabled={saving || !profile.first_name?.trim() || !profile.last_name?.trim()}>
              {saving ? <LoaderCircle size={17} className="portal-spin" /> : <Save size={17} />}
              {language === 'ar' ? 'حفظ التغييرات' : 'Save changes'}
            </button>
          </div>
        </form>

        <aside className="portal-card portal-card-body portal-profile-summary">
          <span className="portal-profile-avatar">{profile.full_name.trim().charAt(0).toUpperCase() || 'S'}</span>
          <h2>{profile.full_name}</h2>
          <p>{profile.email}</p>
          <div className="portal-divider" />
          <div className="portal-profile-completion">
            <div><span>{language === 'ar' ? 'اكتمال الملف' : 'Profile completion'}</span><strong>{completion}%</strong></div>
            <div className="portal-progress"><span style={{ width: `${completion}%` }} /></div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ProfileField({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  readOnly = false,
  hint,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  type?: string;
  required?: boolean;
  readOnly?: boolean;
  hint?: string;
}) {
  return (
    <label className="portal-field">
      <span>{label}{required ? ' *' : ''}</span>
      <input className="portal-input" type={type} value={value} readOnly={readOnly} required={required} onChange={(event) => onChange?.(event.target.value)} />
      {hint && <small className="portal-field-hint">{hint}</small>}
    </label>
  );
}
